import React, {useEffect, useState} from 'react';
import Modal from 'react-modal';
import {ATTRIBUTES, SKILLS, localized} from '../data/traits';
import {DISCIPLINES} from '../data/disciplines';
import {CLANS_BY_ID} from '../data/clans';
import {ADVANTAGE_KIND, MAX_ADVANTAGE_LEVEL} from '../character';
import {
  PURCHASE_TYPES,
  XP_COSTS,
  MAX_TRAIT,
  getXpAvailable,
  getDisciplineMultiplier,
  getPowersForNextDot,
  preparePurchase,
  purchaseSummary,
} from '../progression';
import {vtmT, currentLanguage} from '../i18n';
import {DotPicker} from './creation/CreationParts';
import '../../styles/vtm/roll.css';
import '../../styles/vtm/creation.css';

const EMPTY = {type: '', id: '', skill: '', name: '', description: '', level: 1, powerId: '', index: ''};

const Select = ({id, label, value, onChange, children}) => (
  <div className="vtmField">
    <label className="vtmFieldLabel" htmlFor={id}>{label}</label>
    <select id={id} value={value} onChange={event => onChange(event.target.value)}>
      <option value="">{vtmT('chooseOption')}</option>
      {children}
    </select>
  </div>
);

const Text = ({id, label, value, onChange}) => (
  <div className="vtmField">
    <label className="vtmFieldLabel" htmlFor={id}>{label}</label>
    <input id={id} type="text" value={value} onChange={event => onChange(event.target.value)} />
  </div>
);

/** Achat par XP : attribut, compétence, spécialité, discipline + pouvoir, avantage, Puissance du Sang. */
const VtmXpModal = ({isOpen, onClose, character, actions}) => {
  const lng = currentLanguage();
  const sheet = character.vtm;
  const clan = CLANS_BY_ID[sheet.clan];
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [lastBought, setLastBought] = useState('');
  const set = (patch) => setForm(previous => ({...previous, ...patch}));

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY);
      setLastBought('');
    }
  }, [isOpen]);

  const purchase = {
    type: form.type,
    id: form.id,
    skill: form.skill,
    name: form.name,
    description: form.description,
    level: form.level,
    powerId: form.powerId,
    index: form.index === '' ? -1 : Number(form.index),
  };
  const prepared = form.type ? preparePurchase(sheet, purchase) : null;

  const buy = async () => {
    if (!prepared || !prepared.ok) return;
    setBusy(true);
    try {
      const summary = purchaseSummary(prepared, lng);
      await actions.buyWithXp(prepared, summary);
      setLastBought(summary);
      setForm({...EMPTY, type: form.type});
    } catch (e) {
      console.log(e);
    }
    setBusy(false);
  };

  const traitOption = (item, value, multiplier) => (
    <option key={item.id} value={item.id} disabled={value >= MAX_TRAIT}>
      {localized(item.name, lng)} {value} → {Math.min(value + 1, MAX_TRAIT)}{value < MAX_TRAIT ? ` · ${(value + 1) * multiplier} XP` : ''}
    </option>
  );

  const disciplineTag = (id) => {
    if (!sheet.disciplines[id]) return vtmT('newTag');
    return clan?.disciplines.includes(id) || clan?.anyDisciplines ? vtmT('clanDisciplineTag') : vtmT('outOfClanTag');
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="vtmModal" overlayClassName="vtmModalOverlay" contentLabel={vtmT('spendXp')}>
      <div className="vtmModalHeader">
        <h2 className="vtmModalTitle">{vtmT('spendXp')}</h2>
        <span className="vtmModalSubtitle">{vtmT('xpAvailableShort', {value: getXpAvailable(sheet)})}</span>
      </div>

      {lastBought && <p className="vtmXpBought" role="status">{vtmT('bought', {summary: lastBought})}</p>}

      <form className="vtmRollForm" onSubmit={event => { event.preventDefault(); buy(); }}>
        <Select id="vtmXpType" label={vtmT('purchaseType')} value={form.type} onChange={type => setForm({...EMPTY, type})}>
          {Object.values(PURCHASE_TYPES).map(type => <option key={type} value={type}>{vtmT(`purchase_${type}`)}</option>)}
        </Select>

        {form.type === PURCHASE_TYPES.ATTRIBUTE && (
          <Select id="vtmXpAttribute" label={vtmT('purchase_attribute')} value={form.id} onChange={id => set({id})}>
            {ATTRIBUTES.map(item => traitOption(item, sheet.attributes[item.id], XP_COSTS.attribute))}
          </Select>
        )}

        {form.type === PURCHASE_TYPES.SKILL && (
          <Select id="vtmXpSkill" label={vtmT('purchase_skill')} value={form.id} onChange={id => set({id})}>
            {SKILLS.map(item => traitOption(item, sheet.skills[item.id] || 0, XP_COSTS.skill))}
          </Select>
        )}

        {form.type === PURCHASE_TYPES.SPECIALTY && (
          <>
            <Select id="vtmXpSpecialtySkill" label={vtmT('purchase_skill')} value={form.skill} onChange={skill => set({skill})}>
              {SKILLS.filter(item => sheet.skills[item.id] > 0).map(item => (
                <option key={item.id} value={item.id}>{localized(item.name, lng)}</option>
              ))}
            </Select>
            <Text id="vtmXpSpecialtyName" label={vtmT('specialtyName')} value={form.name} onChange={name => set({name})} />
          </>
        )}

        {form.type === PURCHASE_TYPES.DISCIPLINE && (
          <>
            <Select id="vtmXpDiscipline" label={vtmT('disciplineShort')} value={form.id} onChange={id => set({id, powerId: ''})}>
              {DISCIPLINES.filter(item => item.id !== 'thinBloodAlchemy').map(item => {
                const level = sheet.disciplines[item.id]?.level || 0;
                const cost = (level + 1) * getDisciplineMultiplier(sheet.clan, item.id);
                return (
                  <option key={item.id} value={item.id} disabled={level >= MAX_TRAIT}>
                    {localized(item.name, lng)} {level} → {Math.min(level + 1, MAX_TRAIT)} · {disciplineTag(item.id)}{level < MAX_TRAIT ? ` · ${cost} XP` : ''}
                  </option>
                );
              })}
            </Select>
            {form.id && (
              <Select id="vtmXpPower" label={vtmT('power')} value={form.powerId} onChange={powerId => set({powerId})}>
                {getPowersForNextDot(sheet, form.id).map(power => (
                  <option key={power.id} value={power.id}>
                    {power.level} · {localized(power.name, lng)}{power.rouse ? ` · ${vtmT('rouseCost', {value: power.rouse})}` : ''}
                  </option>
                ))}
              </Select>
            )}
          </>
        )}

        {form.type === PURCHASE_TYPES.NEW_ADVANTAGE && (
          <>
            <Text id="vtmXpAdvantageName" label={vtmT('advantageName')} value={form.name} onChange={name => set({name})} />
            <Text id="vtmXpAdvantageDescription" label={vtmT('advantageDescription')} value={form.description} onChange={description => set({description})} />
            <div className="vtmField">
              <span className="vtmFieldLabel">{vtmT('advantageLevel')}</span>
              <DotPicker value={form.level} min={1} max={MAX_ADVANTAGE_LEVEL} label={vtmT('advantageLevel')} onChange={level => set({level})} />
            </div>
          </>
        )}

        {form.type === PURCHASE_TYPES.ADVANTAGE_DOT && (
          <Select id="vtmXpAdvantageDot" label={vtmT('advantages')} value={form.index} onChange={index => set({index})}>
            {sheet.advantages.map((item, index) => (item.kind === ADVANTAGE_KIND.ADVANTAGE ? (
              <option key={index} value={index} disabled={item.level >= MAX_ADVANTAGE_LEVEL}>
                {item.name} {item.level} → {Math.min(item.level + 1, MAX_ADVANTAGE_LEVEL)} · {XP_COSTS.advantageDot} XP
              </option>
            ) : null))}
          </Select>
        )}

        {form.type && (
          <div className="vtmRollSummary">
            <span>
              {prepared?.ok && <>{purchaseSummary(prepared, lng)} · <strong>{vtmT('xpCost', {cost: prepared.cost})}</strong></>}
              {prepared && !prepared.ok && <span className="vtmXpError">{vtmT(`xpErr_${prepared.reason}`, {cost: prepared.cost})}</span>}
            </span>
            <button type="submit" className="vtmButton primary" disabled={busy || !prepared?.ok}>{vtmT('buy')}</button>
          </div>
        )}
      </form>

      <div className="vtmModalActions">
        <button type="button" className="vtmButton ghost" onClick={onClose}>{vtmT('close')}</button>
      </div>
    </Modal>
  );
};

export default VtmXpModal;
